'use client'

import React, { useEffect, useRef, useState } from 'react'

type LazyMountProps = {
  children: React.ReactNode
  /**
   * Render immediately on first paint (no IO). Useful for above-the-fold sections.
   */
  initiallyVisible?: boolean
  /**
   * IntersectionObserver rootMargin, e.g. '600px 0px' to preload before entering viewport.
   */
  rootMargin?: string
  /**
   * Placeholder reserved space to avoid CLS before mount.
   */
  minHeight?: number | string
  /**
   * Optional placeholder node (e.g. skeleton). If not provided, we render an empty spacer.
   */
  placeholder?: React.ReactNode
}

const LazyMount = ({
  children,
  initiallyVisible = false,
  rootMargin = '600px 0px',
  minHeight = 1,
  placeholder,
}: LazyMountProps) => {
  const [mounted, setMounted] = useState(initiallyVisible)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (mounted) return
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setMounted(true)
      return
    }

    const io = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setMounted(true)
          io.disconnect()
        }
      },
      { rootMargin, threshold: 0 }
    )

    io.observe(el)

    return () => io.disconnect()
  }, [mounted, rootMargin])

  return (
    <div ref={ref} style={!mounted ? { minHeight } : undefined}>
      {mounted ? children : (placeholder ?? null)}
    </div>
  )
}

export default LazyMount
